import React, { useContext, useState } from 'react';
import { useStyletron } from 'baseui';
import { ARTWORK_TYPE, Banner, KIND } from 'baseui/banner';
import { Checkbox, STYLE_TYPE } from 'baseui/checkbox';
import { Combobox } from 'baseui/combobox';
import { Heading, HeadingLevel } from 'baseui/heading';
import { Segment, SegmentedControl } from 'baseui/segmented-control';
import { faWarning } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ScopeRelatesTo } from '@gtintel/platform-types';
import { COLOURS, toTitleCase } from '@gtintel/platform-utils';
import { ProjectContext, ScopeComponentsContext } from '../../ProjectApp/context';
import Editor from '../Editor';
import { AddWithName, List, ListContext } from '../List';
import { Title } from '../Title';
export const scopeRelatesToFromString = str => {
  const member = Object.entries(ScopeRelatesTo).find(([, v]) => v === str);
  if (member === undefined) throw new Error(`${str} is not a member of ScopeRelatesTo`);
  return member[1];
};
export const ScopeComponentItem = ({
  scope,
  isAssigned
}) => {
  const ctx = useContext(ScopeComponentsContext);
  const [css, theme] = useStyletron();
  const onChange = value => ctx.edit(scope.id, {
    name: value
  });
  const applicabilityKeyFromVals = () => {
    if (scope.entity && scope.principal) {
      return applicabilityIdFromLabel('both');
    } else if (scope.entity) {
      return applicabilityIdFromLabel('entity');
    } else {
      return applicabilityIdFromLabel('principal');
    }
  };
  const applicabilityIdFromLabel = str => {
    const found = ctx.applicability().find(applicability => applicability.label === str);
    return found.id;
  };
  const applicabilityLabelFromId = str => {
    const found = ctx.applicability().find(applicability => applicability.id === str);
    return found.label;
  };
  const updateApplicability = name => {
    let data = {};
    switch (name) {
      case 'entity':
        data = {
          entity: true,
          principal: false
        };
        break;
      case 'principal':
        data = {
          entity: false,
          principal: true
        };
        break;
      case 'both':
        data = {
          entity: true,
          principal: true
        };
        break;
    }
    ctx.edit(scope.id, {
      ...data,
      applicability: name
    });
  };
  const serviceTypeNameToId = str => {
    return ctx.serviceTypes().find(st => st.name === str).id;
  };
  const serviceTypeIdToName = id => {
    if (id) {
      return ctx.serviceTypes().find(st => st.id === id).name;
    } else {
      return '';
    }
  };
  const updateServiceType = serviceTypeName => {
    const serviceTypeId = serviceTypeNameToId(serviceTypeName);
    ctx.edit(scope.id, {
      service_type: serviceTypeId
    });
    setActiveServiceType(serviceTypeName);
  };
  const [activeKey, setActiveKey] = useState(scope.relates_to);
  const [applicabilityActiveKey, setApplicabilityActiveKey] = useState(applicabilityKeyFromVals());
  const [activeServiceType, setActiveServiceType] = useState(serviceTypeIdToName(scope.service_type));
  return <div className={css({
    backgroundColor: theme.colors.backgroundPrimary,
    border: '1px solid #e2e2e2',
    borderRadius: '8px',
    margin: '2em 0',
    padding: '14px'
  })}>
      <HeadingLevel>
        <Heading>
          <Title text={scope.name} onChange={onChange} />
        </Heading>

        {isAssigned && <Banner kind={KIND.warning} artwork={{
        icon: () => <FontAwesomeIcon icon={faWarning} size="lg" />,
        type: ARTWORK_TYPE.badge
      }}>
            Changes are restricted because the subject is included in an
            assignment.
          </Banner>}
        <div className={css({
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '16px',
        '@media screen and (max-width: 768px)': {
          gridTemplateColumns: '1fr'
        }
      })}>
          <div>
            <HeadingLevel>
              <Heading>Relates to</Heading>
            </HeadingLevel>
            <SegmentedControl disabled={isAssigned} overrides={{
            Root: {
              style: {
                marginBottom: '14px'
              }
            }
          }} activeKey={activeKey} onChange={({
            activeKey
          }) => {
            const relates_to = scopeRelatesToFromString(activeKey.toString());
            setActiveKey(relates_to);
            ctx.edit(scope.id, {
              relates_to
            });
          }}>
              {ctx.relatesTo().map(i => <Segment key={i.id} label={i.label} />)}
            </SegmentedControl>
          </div>
          <div>
            <HeadingLevel>
              <Heading>Is applicable to</Heading>
            </HeadingLevel>
            <SegmentedControl disabled={isAssigned} overrides={{
            Root: {
              style: {
                marginBottom: '14px'
              }
            }
          }} activeKey={applicabilityActiveKey} onChange={({
            activeKey
          }) => {
            setApplicabilityActiveKey(activeKey.toString());
            const label = applicabilityLabelFromId(activeKey.toString());
            updateApplicability(label);
          }}>
              {ctx.applicability().map(i => <Segment key={i.id} label={toTitleCase(i.label)} data-testid={`${i.label}-segment-${scope.id}`} />)}
            </SegmentedControl>
          </div>
        </div>

        <div className={css({
        margin: '24px 0'
      })}>
          <HeadingLevel>
            <Heading>Service Type</Heading>
          </HeadingLevel>
          <Combobox disabled={isAssigned} overrides={{
          Root: {
            props: {
              'data-testid': `service-type-combobox-${scope.id}`
            }
          }
        }} value={String(activeServiceType)} options={ctx.serviceTypes()} mapOptionToString={option => option.name} onChange={nextValue => updateServiceType(nextValue)} />
        </div>

        <div className={css({
        margin: '24px 0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px'
      })}>
          <div>
            <HeadingLevel>
              <Heading styleLevel={6}>Automated Component</Heading>
            </HeadingLevel>
            <div className={css({
            fontSize: '14px',
            color: theme.colors.contentSecondary
          })}>
              Enable if the component is handled by automation (e.g., assigned
              to cereal bot)
            </div>
          </div>
          <Checkbox disabled={isAssigned} checked={scope.is_automated || false} onChange={e => {
          ctx.edit(scope.id, {
            is_automated: e.currentTarget.checked
          });
        }} checkmarkType={STYLE_TYPE.toggle} overrides={{
          Root: {
            props: {
              'data-testid': `is-automated-toggle-${scope.id}`
            }
          },
          Toggle: {
            style: ({
              $checked
            }) => ({
              backgroundColor: $checked ? COLOURS.black : COLOURS.white
            })
          }
        }} />
        </div>

        <Editor value={scope.description || ''} onChange={newValue => ctx.edit(scope.id, {
        description: newValue
      })} />
      </HeadingLevel>
    </div>;
};
export const ScopeComponentList = () => {
  const ctx = useContext(ScopeComponentsContext);
  const projectCtx = useContext(ProjectContext);
  const anyAssigned = ctx.list().filter(s => projectCtx?.isAssignedScopeComponent(s.id)).length > 0;
  return <HeadingLevel>
      <ListContext.Provider value={ctx}>
        <List inline reorderable={!anyAssigned} items={ctx.list().map(i => ({
        ...i,
        copyable: true,
        removeable: !projectCtx?.isAssignedScopeComponent(i.id)
      }))} renderItem={scope => <ScopeComponentItem isAssigned={projectCtx?.isAssignedScopeComponent(scope.id)} scope={scope} />} />
        <AddWithName />
      </ListContext.Provider>
    </HeadingLevel>;
};